import { useState, useEffect } from 'react';
import { activationCodeApi } from '../services/api';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { SearchInput } from '../components/ui/SearchInput';
import { Badge } from '../components/ui/Badge';
import { Pagination } from '../components/ui/Pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/Table';
import {
  Key,
  Plus,
  Copy,
  User,
  CheckCircle,
  XCircle,
  Trash2,
  Power,
  PowerOff,
  Ticket,
  Hash,
} from 'lucide-react';

interface ActivationCode {
  id: string;
  code: string;
  plan: 'pro' | 'enterprise' | 'ultimate';
  durationDays: number;
  status: 'active' | 'used' | 'disabled' | 'expired';
  createdBy: string;
  createdAt: string;
  expiresAt?: string;
  usedBy?: string;
  usedAt?: string;
  maxUses?: number;
  useCount: number;
  description?: string;
}

export function ActivationCodesPage() {
  const [codes, setCodes] = useState<ActivationCode[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [plan, setPlan] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    plan: 'pro',
    durationDays: 30,
    quantity: 1,
    expiresAt: '',
    maxUses: 1,
    description: '',
    prefix: '',
  });
  const [generatedCodes, setGeneratedCodes] = useState<string[]>([]);
  const [showGeneratedModal, setShowGeneratedModal] = useState(false);

  const fetchCodes = async () => {
    setIsLoading(true);
    try {
      const response = await activationCodeApi.listCodes({
        page,
        pageSize,
        status: status || undefined,
        plan: plan || undefined,
        keyword: searchKeyword || undefined,
      });
      if (response.code === 0) {
        setCodes(response.data.items as ActivationCode[]);
        setTotal(response.data.total);
      }
    } catch (err) {
      console.error('Failed to fetch activation codes:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCodes();
  }, [page, pageSize, status, plan, searchKeyword]);

  const handleCreateCodes = async () => {
    try {
      const response = await activationCodeApi.createCodes({
        plan: createForm.plan,
        durationDays: createForm.durationDays,
        quantity: createForm.quantity,
        expiresAt: createForm.expiresAt || undefined,
        maxUses: createForm.maxUses,
        description: createForm.description,
        prefix: createForm.prefix,
      });
      if (response.code === 0) {
        setGeneratedCodes(response.data.codes);
        setShowCreateModal(false);
        setShowGeneratedModal(true);
        fetchCodes();
      }
    } catch (err) {
      console.error('Failed to create activation codes:', err);
    }
  };

  const handleDisableCode = async (id: string) => {
    if (!confirm('确定要禁用此激活码吗？')) return;
    try {
      await activationCodeApi.disableCode(id);
      fetchCodes();
    } catch (err) {
      console.error('Failed to disable activation code:', err);
    }
  };

  const handleEnableCode = async (id: string) => {
    try {
      await activationCodeApi.enableCode(id);
      fetchCodes();
    } catch (err) {
      console.error('Failed to enable activation code:', err);
    }
  };

  const handleDeleteCode = async (id: string) => {
    if (!confirm('确定要删除此激活码吗？此操作不可恢复。')) return;
    try {
      await activationCodeApi.deleteCode(id);
      fetchCodes();
    } catch (err) {
      console.error('Failed to delete activation code:', err);
    }
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    alert('激活码已复制到剪贴板');
  };

  const handleCopyAllCodes = () => {
    navigator.clipboard.writeText(generatedCodes.join('\n'));
    alert('所有激活码已复制到剪贴板');
  };

  const formatDate = (date?: string) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('zh-CN');
  };

  const getPlanBadge = (plan: string) => {
    switch (plan) {
      case 'pro':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-600">
            <Ticket className="w-3 h-3" />
            专业版
          </span>
        );
      case 'enterprise':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-600">
            <Ticket className="w-3 h-3" />
            企业版
          </span>
        );
      case 'ultimate':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-600">
            <Ticket className="w-3 h-3" />
            旗舰版
          </span>
        );
      default:
        return <Badge>{plan}</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="success">未使用</Badge>;
      case 'used':
        return <Badge variant="info">已使用</Badge>;
      case 'disabled':
        return <Badge variant="error">已禁用</Badge>;
      case 'expired':
        return <Badge variant="warning">已过期</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getStats = () => {
    const totalCodes = codes.length;
    const activeCodes = codes.filter(c => c.status === 'active').length;
    const usedCodes = codes.filter(c => c.status === 'used').length;
    const disabledCodes = codes.filter(c => c.status === 'disabled').length;
    return { totalCodes, activeCodes, usedCodes, disabledCodes };
  };

  const stats = getStats();

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">激活码管理</h1>
          <p className="text-zinc-500 mt-1">创建和管理订阅激活码</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4" />
          生成激活码
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-500">总激活码</p>
                <p className="text-2xl font-bold text-zinc-900 mt-1">{stats.totalCodes}</p>
              </div>
              <div className="p-3 bg-zinc-100 rounded-xl">
                <Hash className="w-6 h-6 text-zinc-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-500">未使用</p>
                <p className="text-2xl font-bold text-success-600 mt-1">{stats.activeCodes}</p>
              </div>
              <div className="p-3 bg-success-100 rounded-xl">
                <CheckCircle className="w-6 h-6 text-success-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-500">已使用</p>
                <p className="text-2xl font-bold text-accent-600 mt-1">{stats.usedCodes}</p>
              </div>
              <div className="p-3 bg-accent-100 rounded-xl">
                <User className="w-6 h-6 text-accent-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-500">已禁用</p>
                <p className="text-2xl font-bold text-error-600 mt-1">{stats.disabledCodes}</p>
              </div>
              <div className="p-3 bg-error-100 rounded-xl">
                <XCircle className="w-6 h-6 text-error-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <SearchInput
              placeholder="搜索激活码..."
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              className="sm:w-64"
            />
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="px-3 py-2 rounded-lg border border-zinc-300 bg-white text-zinc-900"
            >
              <option value="">所有状态</option>
              <option value="active">未使用</option>
              <option value="used">已使用</option>
              <option value="disabled">已禁用</option>
              <option value="expired">已过期</option>
            </select>
            <select
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
              className="px-3 py-2 rounded-lg border border-zinc-300 bg-white text-zinc-900"
            >
              <option value="">所有套餐</option>
              <option value="pro">专业版</option>
              <option value="enterprise">企业版</option>
              <option value="ultimate">旗舰版</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Codes Table */}
      <Card>
        <CardHeader>
          <CardTitle>激活码列表</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-600"></div>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>激活码</TableHead>
                    <TableHead>套餐</TableHead>
                    <TableHead>时长</TableHead>
                    <TableHead>状态</TableHead>
                    <TableHead>使用次数</TableHead>
                    <TableHead>使用者</TableHead>
                    <TableHead>创建时间</TableHead>
                    <TableHead>到期时间</TableHead>
                    <TableHead className="text-right">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {codes.map((code) => (
                    <TableRow key={code.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Key className="w-4 h-4 text-zinc-400" />
                          <code className="bg-zinc-100 px-2 py-1 rounded text-sm font-mono">
                            {code.code}
                          </code>
                          <button
                            onClick={() => handleCopyCode(code.code)}
                            className="p-1 hover:bg-zinc-100 rounded"
                            title="复制"
                          >
                            <Copy className="w-3 h-3 text-zinc-400" />
                          </button>
                        </div>
                      </TableCell>
                      <TableCell>{getPlanBadge(code.plan)}</TableCell>
                      <TableCell>
                        <span className="text-sm text-zinc-600">{code.durationDays} 天</span>
                      </TableCell>
                      <TableCell>{getStatusBadge(code.status)}</TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {code.useCount} / {code.maxUses || 1}
                        </span>
                      </TableCell>
                      <TableCell>
                        {code.usedBy ? (
                          <div className="flex items-center gap-1 text-sm">
                            <User className="w-3 h-3 text-zinc-400" />
                            <span className="text-zinc-600">{code.usedBy.slice(0, 8)}...</span>
                          </div>
                        ) : (
                          <span className="text-zinc-400 text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(code.createdAt)}</TableCell>
                      <TableCell>{formatDate(code.expiresAt)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {code.status === 'active' && (
                            <button
                              onClick={() => handleDisableCode(code.id)}
                              className="p-2 text-zinc-400 hover:text-warning-600 hover:bg-warning-50 rounded-lg transition-colors"
                              title="禁用"
                            >
                              <PowerOff className="w-4 h-4" />
                            </button>
                          )}
                          {code.status === 'disabled' && (
                            <button
                              onClick={() => handleEnableCode(code.id)}
                              className="p-2 text-zinc-400 hover:text-success-600 hover:bg-success-50 rounded-lg transition-colors"
                              title="启用"
                            >
                              <Power className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteCode(code.id)}
                            className="p-2 text-zinc-400 hover:text-error-600 hover:bg-error-50 rounded-lg transition-colors"
                            title="删除"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Pagination
                currentPage={page}
                totalPages={Math.ceil(total / pageSize)}
                onPageChange={setPage}
                totalItems={total}
                pageSize={pageSize}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-zinc-900 mb-4">生成激活码</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">套餐类型</label>
                <select
                  value={createForm.plan}
                  onChange={(e) => setCreateForm({ ...createForm, plan: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-zinc-300"
                >
                  <option value="pro">专业版</option>
                  <option value="enterprise">企业版</option>
                  <option value="ultimate">旗舰版</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">订阅时长（天）</label>
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={createForm.durationDays}
                  onChange={(e) => setCreateForm({ ...createForm, durationDays: parseInt(e.target.value) || 1 })}
                  className="w-full px-3 py-2 rounded-lg border border-zinc-300"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">生成数量</label>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={createForm.quantity}
                  onChange={(e) => setCreateForm({ ...createForm, quantity: parseInt(e.target.value) || 1 })}
                  className="w-full px-3 py-2 rounded-lg border border-zinc-300"
                />
                <p className="text-xs text-zinc-500 mt-1">一次最多生成 100 个</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">最大使用次数</label>
                <input
                  type="number"
                  min={1}
                  max={1000}
                  value={createForm.maxUses}
                  onChange={(e) => setCreateForm({ ...createForm, maxUses: parseInt(e.target.value) || 1 })}
                  className="w-full px-3 py-2 rounded-lg border border-zinc-300"
                />
                <p className="text-xs text-zinc-500 mt-1">每个激活码可以被使用的次数</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">激活码前缀（可选）</label>
                <input
                  type="text"
                  value={createForm.prefix}
                  onChange={(e) => setCreateForm({ ...createForm, prefix: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 rounded-lg border border-zinc-300 uppercase"
                  placeholder="如: PRO-"
                  maxLength={10}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">激活码有效期（可选）</label>
                <input
                  type="date"
                  value={createForm.expiresAt}
                  onChange={(e) => setCreateForm({ ...createForm, expiresAt: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-zinc-300"
                />
                <p className="text-xs text-zinc-500 mt-1">超过此日期后激活码将失效</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">备注（可选）</label>
                <textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-zinc-300"
                  rows={2}
                  placeholder="如: 推广活动专用..."
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
                取消
              </Button>
              <Button onClick={handleCreateCodes}>
                生成激活码
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Generated Codes Modal */}
      {showGeneratedModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-zinc-900">激活码已生成</h3>
              <Button variant="secondary" size="sm" onClick={handleCopyAllCodes}>
                <Copy className="w-4 h-4 mr-1" />
                复制全部
              </Button>
            </div>
            <p className="text-sm text-zinc-500 mb-4">
              成功生成 {generatedCodes.length} 个激活码，请妥善保存！
            </p>
            <div className="bg-zinc-50 rounded-lg p-4 max-h-80 overflow-y-auto">
              <div className="space-y-2">
                {generatedCodes.map((code, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-white px-3 py-2 rounded border border-zinc-200"
                  >
                    <code className="font-mono text-sm">{code}</code>
                    <button
                      onClick={() => handleCopyCode(code)}
                      className="p-1 hover:bg-zinc-100 rounded"
                    >
                      <Copy className="w-4 h-4 text-zinc-400" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end mt-6">
              <Button onClick={() => setShowGeneratedModal(false)}>
                完成
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
